import React, { useState } from 'react';
import { Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', amber: '#E8A020', red: '#E24B4A', slate: '#5C6B7A' };
const money = (v) => (v || v === 0 ? `$${Number(v).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—');

export default function QuantumRow({ claim: c, sources, api, onOpen, onDone }) {
  const [amount, setAmount] = useState('');
  const [basis, setBasis] = useState('');
  const [source, setSource] = useState('assessor_report');
  const [align, setAlign] = useState(true);
  const [busy, setBusy] = useState(false);

  const record = async () => {
    const q = parseFloat(amount);
    if (!(q > 0)) { toast.error('Enter the quantum — a positive dollar amount'); return; }
    if (basis.trim().length < 10) { toast.error('Say what the figure rests on (at least 10 characters)'); return; }
    setBusy(true);
    try {
      const r = await api.post(`/claims/${c.claim_id}/quantum`, { quantum: q, basis: basis.trim(), source, align_reserve: align });
      toast.success(`Quantum ${money(q)} recorded on ${c.claim_reference || c.claim_number}${align ? ` · reserve now ${money(r.data.reserve_amount)}` : ''}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not record the quantum'); }
    finally { setBusy(false); }
  };

  const net = parseFloat(amount) > 0 ? Math.max(parseFloat(amount) - (c.excess || 0), 0) : null;
  return (
    <tr className="border-t align-top" style={{ borderColor: 'rgba(136,150,168,0.15)' }} data-testid={`quantum-row-${c.claim_id}`}>
      <td className="py-2 pr-2">
        <button onClick={() => onOpen(c.claim_id)} className="font-mono text-left hover:underline" style={{ color: C.blue }} data-testid={`quantum-open-${c.claim_id}`}>{c.claim_reference || c.claim_number}</button>
        <div className="text-[10px]" style={{ color: C.slate }}>{c.policy_number} · {c.claim_type} · {c.days_open}d open</div>
      </td>
      <td className="py-2 pr-2 max-w-[220px]" style={{ color: C.navy }}>
        <div className="truncate">{c.business_name}</div>
        <div className="text-[10px] truncate" style={{ color: C.slate }} title={c.description}>{c.description}</div>
      </td>
      <td className="py-2 pr-2 capitalize whitespace-nowrap" style={{ color: C.slate }}>{(c.status || '').replace(/_/g, ' ')}<div className="text-[10px]">{c.handler_name || '—'}</div></td>
      <td className="py-2 pr-2 text-right whitespace-nowrap" style={{ color: C.navy }}>{money(c.reserve_amount)}<div className="text-[10px]" style={{ color: C.slate }}>excess {money(c.excess || 0)}</div></td>
      <td className="py-2 pr-2 text-right whitespace-nowrap" style={{ color: c.idle_days >= 60 ? C.red : C.amber }}>{c.idle_days}d</td>
      <td className="py-2 pl-3" onClick={e => e.stopPropagation()}>
        <div className="flex flex-wrap items-center gap-1">
          <input type="number" min="0" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder={c.hint ? `e.g. ${Number(c.hint.amount).toFixed(2)}` : 'quantum $'} className="text-[11px] border rounded-sm px-1.5 py-1 bg-white w-28" data-testid={`quantum-amount-${c.claim_id}`} />
          <select value={source} onChange={e => setSource(e.target.value)} className="text-[11px] border rounded-sm px-1.5 py-1 bg-white" data-testid={`quantum-source-${c.claim_id}`}>
            {Object.entries(sources || {}).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <input value={basis} onChange={e => setBasis(e.target.value)} onKeyDown={e => e.key === 'Enter' && record()} placeholder="what the figure rests on…" className="text-[11px] border rounded-sm px-1.5 py-1 bg-white w-56" data-testid={`quantum-basis-${c.claim_id}`} />
          <label className="flex items-center gap-1 text-[10px] whitespace-nowrap" style={{ color: C.slate }} title="Move the reserve to quantum less excess when the quantum is recorded">
            <input type="checkbox" checked={align} onChange={e => setAlign(e.target.checked)} data-testid={`quantum-align-${c.claim_id}`} /> reserve follows{net !== null && align ? ` → ${money(net)}` : ''}
          </label>
          <button onClick={record} disabled={busy} className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-sm text-white disabled:opacity-50" style={{ background: C.blue }} data-testid={`quantum-record-${c.claim_id}`}>
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} Record
          </button>
        </div>
        {c.hint && <p className="text-[10px] mt-1" style={{ color: C.slate }} data-testid={`quantum-hint-${c.claim_id}`}>On file: {money(c.hint.amount)} — {c.hint.from}</p>}
      </td>
    </tr>
  );
}
